import { Navigate, Outlet } from 'react-router'
import { useAuth } from '@/lib/auth'
import { useTrainingStatus } from '@/hooks/useTrainingStatus'

interface ProtectedRouteProps {
  children?: React.ReactNode
  /**
   * When true the training gate is skipped — used only for the
   * /onboarding/training route itself so agents can reach it before completing.
   */
  skipTrainingGate?: boolean
}

function Loader() {
  return (
    <div className="flex h-screen items-center justify-center bg-background">
      <div className="text-center">
        <h1 className="text-xl font-bold tracking-[-0.04em]">
          rize<span className="text-primary">.</span>
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">Loading...</p>
      </div>
    </div>
  )
}

/**
 * The training read FAILED, so we do not know whether this user still owes
 * mandatory training.
 *
 * ⚠️ THIS IS A SCREEN AND NOT A `<Navigate>`, deliberately. Redirecting to
 * /onboarding/training here would send the user to a page backed by the SAME
 * failing query — no modules to render, nothing to acknowledge, and ProtectedRoute
 * re-evaluating on every attempt. That is a permanent lockout, which is the mig 201
 * failure this codebase has already paid for once. Letting them THROUGH is the
 * opposite failure: a silent compliance bypass on a network blip.
 *
 * So: stop, say so plainly, and offer the retry. Neither passed nor trapped.
 */
function TrainingUnavailable({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex h-screen items-center justify-center bg-background p-6">
      <div className="max-w-sm text-center">
        <h1 className="text-xl font-bold tracking-[-0.04em]">
          rize<span className="text-primary">.</span>
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          We couldn&rsquo;t check your training status just now. This is usually a
          connection problem.
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-4 min-h-[44px] rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:opacity-90"
        >
          Try again
        </button>
      </div>
    </div>
  )
}

export function ProtectedRoute({ children, skipTrainingGate = false }: ProtectedRouteProps) {
  const { user, loading, memberships, membershipsLoaded } = useAuth()
  const {
    allComplete,
    loading: trainingLoading,
    error: trainingError,
    refetch: refetchTraining,
  } = useTrainingStatus()

  if (loading || (!skipTrainingGate && trainingLoading)) {
    return <Loader />
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  // Authenticated, but this user's memberships haven't resolved yet. Wait instead of
  // reading the still-empty initial array as "no org" — that race bounced org-having
  // members to /onboarding (create-team) immediately after login.
  if (!membershipsLoaded) {
    return <Loader />
  }

  if (memberships.length === 0) {
    return <Navigate to="/onboarding" replace />
  }

  // The training read failed. Checked BEFORE the gate below, because the gate's
  // redirect is exactly the wrong response to an unknown state — see
  // TrainingUnavailable. Skipped routes stay skipped: /onboarding/training must
  // still render its own error rather than being shadowed by this one.
  if (!skipTrainingGate && trainingError) {
    return <TrainingUnavailable onRetry={refetchTraining} />
  }

  // Training gate: redirect to mandatory onboarding before the app shell loads
  if (!skipTrainingGate && !allComplete) {
    return <Navigate to="/onboarding/training" replace />
  }

  // Layout route: if children provided, render them; otherwise render Outlet
  // (used when ProtectedRoute is the layout element for a nested route group)
  return children ? <>{children}</> : <Outlet />
}
